import React, { useEffect, useRef, useState } from 'react';
import { VIDEO_MAX_SECONDS } from '../../../services/exploreDrops';

const MIN_RECORD_SECONDS = 0.4;
const RING_SIZE = 78;
const RING_STROKE = 5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function pickRecorderMimeType() {
  const candidates = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/** Prefer ultra-wide / wider FOV (~0.6x) when the device exposes a zoom capability. */
async function preferWideCameraZoom(stream, targetZoom = 0.6) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track?.getCapabilities) return;
  let caps;
  try {
    caps = track.getCapabilities();
  } catch {
    return;
  }
  if (!caps || caps.zoom == null) return;
  const min = Number(caps.zoom.min);
  const max = Number(caps.zoom.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return;
  const zoom = Math.min(max, Math.max(min, targetZoom));
  try {
    await track.applyConstraints({ advanced: [{ zoom }] });
  } catch {
    /* Desktop / unsupported — keep default FOV */
  }
}

function formatSeconds(total) {
  const s = Math.max(0, Math.min(VIDEO_MAX_SECONDS, total));
  return s.toFixed(1);
}

/**
 * Instagram Stories–style in-sheet recorder: one shutter circle + 7s progress ring.
 */
const GameMediaCapture = ({ open, onClose, onCaptured, onError }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const hardStopRef = useRef(null);
  const startedAtRef = useRef(0);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setError('');
    setReady(false);
    setRecording(false);
    setElapsed(0);
    chunksRef.current = [];

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera is not available in this browser.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        await preferWideCameraZoom(stream, 0.6);
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const el = videoRef.current;
        if (el) {
          el.srcObject = stream;
          await el.play().catch(() => {});
        }
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          const message =
            err?.name === 'NotAllowedError'
              ? 'Allow camera and microphone to record a video.'
              : err?.message || 'Could not open camera.';
          setError(message);
          onError?.(err instanceof Error ? err : new Error(message));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (hardStopRef.current) window.clearTimeout(hardStopRef.current);
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      recorderRef.current = null;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const finishRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
  };

  const handleStart = () => {
    setError('');
    const stream = streamRef.current;
    if (!stream) {
      const message = 'Camera is not ready yet.';
      setError(message);
      onError?.(new Error(message));
      return;
    }
    const mimeType = pickRecorderMimeType();
    if (!mimeType) {
      const message = 'Recording is not supported in this browser.';
      setError(message);
      onError?.(new Error(message));
      return;
    }

    chunksRef.current = [];
    let recorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 1_200_000,
      });
    } catch (err) {
      const message = 'Could not start recording.';
      setError(message);
      onError?.(err instanceof Error ? err : new Error(message));
      return;
    }

    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data?.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (hardStopRef.current) window.clearTimeout(hardStopRef.current);
      const recordedFor = (Date.now() - startedAtRef.current) / 1000;
      setRecording(false);
      setElapsed(0);
      const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
      chunksRef.current = [];
      recorderRef.current = null;
      if (!blob.size || recordedFor < MIN_RECORD_SECONDS) {
        setError('Hold a bit longer — tap again to record.');
        return;
      }
      const file = new File([blob], 'capture.webm', { type: 'video/webm' });
      onCaptured?.(file);
      onClose?.();
    };

    recorder.start(200);
    setRecording(true);
    setElapsed(0);
    startedAtRef.current = Date.now();
    tickRef.current = window.setInterval(() => {
      setElapsed((Date.now() - startedAtRef.current) / 1000);
    }, 50);
    hardStopRef.current = window.setTimeout(() => {
      finishRecording();
    }, VIDEO_MAX_SECONDS * 1000);
  };

  const handleShutter = () => {
    if (!ready) return;
    if (recording) finishRecording();
    else handleStart();
  };

  const progress = Math.min(1, elapsed / VIDEO_MAX_SECONDS);
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    <div className="game-capture" role="dialog" aria-modal="true" aria-label="Record video">
      <button
        type="button"
        className="game-capture__backdrop"
        aria-label="Close recorder"
        onClick={onClose}
      />
      <div className="game-capture__panel game-capture__panel--story">
        <button
          type="button"
          className="game-capture__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="game-capture__stage game-capture__stage--story">
          <video
            ref={videoRef}
            className="game-capture__preview game-capture__preview--story"
            playsInline
            muted
            autoPlay
          />

          <div className="game-capture__shutter-wrap">
            {recording ? (
              <p className="game-capture__elapsed" role="status" aria-live="polite">
                {formatSeconds(elapsed)}s / {VIDEO_MAX_SECONDS}s
              </p>
            ) : (
              <p className="game-capture__hint">
                {ready ? `Tap to record · max ${VIDEO_MAX_SECONDS}s` : 'Opening camera…'}
              </p>
            )}

            <button
              type="button"
              className={`game-capture__shutter${recording ? ' is-recording' : ''}${
                !ready ? ' is-disabled' : ''
              }`}
              disabled={!ready}
              aria-label={recording ? 'Stop recording' : 'Start recording'}
              onClick={handleShutter}
            >
              <svg
                className="game-capture__ring"
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                aria-hidden="true"
              >
                <circle
                  className="game-capture__ring-track"
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  strokeWidth={RING_STROKE}
                />
                <circle
                  className="game-capture__ring-progress"
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  strokeWidth={RING_STROKE}
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={recording ? dashOffset : RING_CIRCUMFERENCE}
                  transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                />
              </svg>
              <span className="game-capture__shutter-core" aria-hidden="true" />
            </button>
          </div>
        </div>

        {error ? (
          <p className="game-capture__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default GameMediaCapture;
